import globalLogger from "@lazarentertainment/js-logger"
import { useCallback, useEffect, useState } from "react"

import { defaultVideoState } from "./VideoState"

export type MutedAutoplayProps = {
  videoElement: HTMLVideoElement | null

  /**
   * Determines how this hook should behave.
   *
   * "unmuted-with-fallback-to-muted":
   *  - attempts videoElement.play(), and if it fails, mutes videoElement and tries again.
   *
   * "unmuted-with-fallback-to-paused":
   *  - attempts videoElement.play(), and if it fails, does nothing else.
   *
   * "muted":
   *  - mutes videoElement then attempts videoElement.play(),
   *    it should succeed since the browser always allows muted video to autoplay.
   *
   * @default "unmuted-with-fallback-to-muted"
   */
  autoplayStrategy:
    | "unmuted-with-fallback-to-muted"
    | "unmuted-with-fallback-to-paused"
    | "muted"

  /**
   * These are needed in order to know whether the video should be played.
   */
  canPlay: boolean
  live: boolean
}

export const useAutoplay = ({
  videoElement,
  autoplayStrategy = "unmuted-with-fallback-to-muted",
  canPlay,
  live,
}: MutedAutoplayProps) => {
  const [mutedPreview, setMutedPreview] = useState(
    defaultVideoState.mutedPreview,
  )

  // Detect whether the video is ever playing in an unmuted state.
  // If so, we set 'mutedPreview' to false, indicating that full video playback has begun.
  const onVolumeChangeOrPlay = useCallback(
    (event: Event) => {
      const payload = event.currentTarget as HTMLVideoElement
      if (
        mutedPreview &&
        payload.paused === false &&
        payload.muted === false
      ) {
        setMutedPreview(false)
      }
    },
    [mutedPreview],
  )

  const shouldAttemptAutoplay = live && canPlay

  // This is the effect that performs the autoplaying logic:
  useEffect(() => {
    // Don't do anything if it's not time to autoplay
    if (
      !shouldAttemptAutoplay ||
      !videoElement ||
      videoElement.paused === false
    ) {
      return
    }

    if (autoplayStrategy === "muted") {
      videoElement.muted = true
    }
    videoElement?.play().catch((exception: DOMException) => {
      // Autoplay denied by the browser - Perform fallback behavior
      globalLogger.info(`Autoplay denied: ${exception}`)
      if (autoplayStrategy === "unmuted-with-fallback-to-muted") {
        videoElement.muted = true
        videoElement?.play().catch((exception: DOMException) => {
          globalLogger.info(`Autoplay denied after muting: ${exception}`)
        })
      }
    })
  }, [videoElement, autoplayStrategy, shouldAttemptAutoplay])

  useEffect(() => {
    videoElement?.addEventListener("volumechange", onVolumeChangeOrPlay)
    videoElement?.addEventListener("play", onVolumeChangeOrPlay)
    return () => {
      videoElement?.removeEventListener(
        "volumechange",
        onVolumeChangeOrPlay,
      )
      videoElement?.removeEventListener("play", onVolumeChangeOrPlay)
    }
  }, [videoElement, onVolumeChangeOrPlay])

  return { mutedPreview, setMutedPreview }
}
